import { IntroIFrame, IntroWrapper } from './__intro-elements';
import { HelpModeReminder, StartTour, TutorialsReminder } from './_intro-app';
import { IntroKeys } from './intro-keys';

const NavSb = () => (
  <IntroWrapper>
    <p>
      <b>Storyboard</b> page: <b>Arrange clips</b> to tell your story.
    </p>
  </IntroWrapper>
);

const SbIntro = () => (
  <IntroWrapper size="medium">
    <p>
      <b>Storyboarding</b> is a visual planning tool used in film and other media productions to pre-visualize a
      sequence of events or scenes.
    </p>
    <p>
      The <b>Pulse Storyboard</b> contains your highlights arranged in a sequence of clips to represent how your story
      will unfold. Each clip contains a highlight, helping to plan the visuals, camera angles, timing, and flow of your
      story before rendering the final cut.
    </p>
    <IntroIFrame src="https://youtube.com/embed/Wl3PwIB-DuU" />
  </IntroWrapper>
);

const StoryboardSettings = () => (
  <IntroWrapper>
    <p>
      <b>Storyboard Settings:</b> Adjust title, credits, aspect ratio, transitions, and more for your story.
    </p>
  </IntroWrapper>
);

const SbAudio = () => (
  <IntroWrapper>
    <p>
      <b>Music Track</b>: Pulse uses the beats of the music to define the clip durations. Beat markers denote the
      location of beats.
    </p>
  </IntroWrapper>
);

const StartRangeMarker = () => (
  <IntroWrapper>
    <p>
      <b>Start Range Marker</b>: Drag this marker to define where your storyboard begins.
    </p>
  </IntroWrapper>
);

const EndRangeMarker = () => (
  <IntroWrapper>
    <p>
      <b>End Range Marker</b>: Drag this marker to define where your storyboard ends.
    </p>
  </IntroWrapper>
);

const StoryboardTrack = () => (
  <IntroWrapper size="medium">
    <p>
      The <b>Storyboard Track</b> is the heart of your video editing workflow. Here you can:
    </p>
    <ul>
      {/* TODO: Add this back when drag and drop from Highlights is implemented */}
      {/* <li>Drag and drop clips from the Highlights page</li> */}
      <li>Arrange the clips to tell your story</li>
      <li>Preview your story before rendering</li>
      <li>Fine-tune clip timing and effects</li>
    </ul>
  </IntroWrapper>
);

const StoryboardZoom = () => (
  <IntroWrapper size="medium">
    <p>
      <b>Zoom:</b> Initially, the Storyboard is zoomed out to show the entire timeline. Clips will be stacked on top of
      each other and difficult to see.
    </p>
    <p>
      Use your <b>mouse wheel</b> or <b>touch pad</b> to zoom into the track. This will make it easier to manage and
      configure your clips.
    </p>
    <p>
      <i>Give it a try!</i>
    </p>
    <IntroIFrame src="https://www.youtube.com/embed/TTnMuFBKB8U" />
  </IntroWrapper>
);

const ClipboardTrack = () => (
  <IntroWrapper>
    <p>
      Use the <b>Clipboard</b> Track to stage sequences.
    </p>
  </IntroWrapper>
);

const DiscardboardTrack = () => (
  <IntroWrapper>
    <p>
      Use the <b>Discardboard (Cutting Room Floor)</b> Track to discard clips you are not quite ready to delete yet.
    </p>
  </IntroWrapper>
);

const Cell = () => (
  <IntroWrapper size="medium">
    <p>
      <b>Clips</b> are placed in storyboard cells.
    </p>
    <p>
      The duration is automatically set by the music beats in the Music Track above, spanning from one beat marker to
      the next, ensuring perfect synchronization.
    </p>
  </IntroWrapper>
);

const ClipEffects = () => (
  <IntroWrapper>
    <p>
      Click the <b>Settings Gear</b> on the clip to configure <b>effects</b>.
    </p>
  </IntroWrapper>
);

const SbPlayback = () => (
  <IntroWrapper>
    <p>
      <b>Preview</b> your Storyboard before rendering.
    </p>
  </IntroWrapper>
);

const SbRender = () => (
  <IntroWrapper>
    <p>
      When you are finished editing your storyboard, <b>render</b> to a Final Cut.
    </p>
    <p>Don't worry, you can always come back, make additional changes, and re-render.</p>
  </IntroWrapper>
);

const NavFc = () => (
  <IntroWrapper>
    <p>
      After you render you will be taken to the <b>Final Cuts</b> page. Or you can navigate there if you already have a
      Final Cut.{' '}
    </p>
  </IntroWrapper>
);

export const STEPS_SB = [
  { element: `[data-intro="${IntroKeys.navSb}"]`, intro: <NavSb /> },
  { element: `[data-intro="${IntroKeys.navHelpMode}"]`, intro: <HelpModeReminder /> },
  { element: `[data-intro="${IntroKeys.navStartTour}"]`, intro: <StartTour /> },
  { element: `[data-intro="${IntroKeys.navTutorials}"]`, intro: <TutorialsReminder /> },
  { intro: <SbIntro /> },
  { element: `[data-intro="${IntroKeys.sbSettings}"]`, intro: <StoryboardSettings /> },
  { element: `[data-intro="${IntroKeys.sbAudio}"]`, intro: <SbAudio /> },
  { element: `[data-intro="${IntroKeys.startRangeMarker}"]`, intro: <StartRangeMarker /> },
  { element: `[data-intro="${IntroKeys.endRangeMarker}"]`, intro: <EndRangeMarker /> },
  { element: `[data-intro="${IntroKeys.storyBoard}"]`, intro: <StoryboardTrack /> },
  { element: `[data-intro="${IntroKeys.storyBoard}"]`, intro: <StoryboardZoom /> },
  { element: `[data-intro="${IntroKeys.clipBoard}"]`, intro: <ClipboardTrack /> },
  { element: `[data-intro="${IntroKeys.discardBoard}"]`, intro: <DiscardboardTrack /> },
  { element: `[data-intro="${IntroKeys.sbClip}"]`, intro: <Cell /> },
  { element: `[data-intro="${IntroKeys.sbClip}"]`, intro: <ClipEffects /> },
  { element: `[data-intro="${IntroKeys.sbPlay}"]`, intro: <SbPlayback /> },
  { element: `[data-intro="${IntroKeys.sbRender}"]`, intro: <SbRender /> },
  { element: `[data-intro="${IntroKeys.navFc}"]`, intro: <NavFc /> },
];
